import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Button, 
  Tabs, 
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import BusinessIcon from '@mui/icons-material/Business';
import HomeIcon from '@mui/icons-material/Home';
import DomainIcon from '@mui/icons-material/Domain';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { brand, pageHeroRichSxFor, pageElevatedCardSx, pageHeroImages, pageHeroType, servicesHeroMinHeight } from '../brandTheme';

const servicesData = [
  {
    id: 0,
    title: 'Personal Loan',
    icon: <AccountBalanceIcon />,
    shortDesc: 'Quick funds for your personal needs.',
    description: 'A Personal Loan is an unsecured loan that provides you with funds to meet your personal financial needs. Whether you are planning a dream wedding, a vacation, renovating your home, or dealing with a medical emergency, our Personal Loan is the perfect solution.',
    highlights: [
      { label: 'Interest Rate', value: '10.99% - 21.00% p.a.' },
      { label: 'Loan Amount', value: '₹50,000 - ₹40 Lakhs' },
      { label: 'Tenure', value: '12 - 60 Months' },
      { label: 'Processing Fee', value: 'Up to 2.5% + GST' },
    ],
    features: [
      'Collateral-free loan: No security or guarantor required.',
      'Flexible Repayment: Choose a tenure that suits your pocket.',
      'Quick Disbursal: Get funds in your account within 24 hours.',
      'Minimal Documentation: Paperless process for pre-approved customers.'
    ],
    eligibility: [
      { criteria: 'Age', value: '21 years to 60 years' },
      { criteria: 'Employment', value: 'Salaried or Self-Employed' },
      { criteria: 'Min. Income', value: '₹25,000 per month' },
      { criteria: 'CIBIL Score', value: '700 and above' },
      { criteria: 'Work Exp.', value: 'Min. 1 year total, 6 months in current org.' }
    ],
    documents: [
      'Identity Proof (Aadhar/PAN/Passport)',
      'Address Proof (Utility Bill/Rent Agreement)',
      'Last 3 months Salary Slips',
      'Last 6 months Bank Statements',
      'Passport size photographs'
    ],
    faqs: [
      { q: 'How much personal loan can I get?', a: 'You can avail a loan up to ₹40 Lakhs depending on your income and credit score.' },
      { q: 'Is there a prepayment penalty?', a: 'Yes, foreclosure charges may apply ranging from 2-4% of the outstanding principal after a lock-in period.' },
      { q: 'Can I apply with a low CIBIL score?', a: 'A score of 700+ is recommended. Lower scores may attract higher interest rates or rejection.' }
    ]
  },
  {
    id: 1,
    title: 'Business Loan',
    icon: <BusinessIcon />,
    shortDesc: 'Capital to grow your business.',
    description: 'Fuel your business ambitions with our customized Business Loans. Designed for SMEs, startups, and established enterprises, this loan helps in working capital management, purchasing machinery, infrastructure expansion, or hiring new staff.',
    highlights: [
      { label: 'Interest Rate', value: '15.00% - 24.00% p.a.' },
      { label: 'Loan Amount', value: '₹1 Lakh - ₹50 Lakhs' },
      { label: 'Tenure', value: '12 - 48 Months' },
      { label: 'Processing Fee', value: 'Up to 3% + GST' },
    ],
    features: [
      'Unsecured funding: No collateral required.',
      'Overdraft Facility: Pay interest only on the utilized amount.',
      'Fast Approval: Decisions within 48 hours.',
      'Tax Benefits: Interest paid is tax-deductible as a business expense.'
    ],
    eligibility: [
      { criteria: 'Business Vintage', value: 'Minimum 3 years' },
      { criteria: 'Turnover', value: '₹40 Lakhs minimum annual turnover' },
      { criteria: 'Profitability', value: 'Business should be profitable for last 2 years' },
      { criteria: 'Age', value: '24 years to 65 years' },
      { criteria: 'ITR', value: 'Filed for last 2 years' }
    ],
    documents: [
      'Business KYC (PAN, Registration Certificate)',
      'GST Returns (Last 12 months)',
      'ITR with Computation (Last 2 years)',
      'Bank Statements (Last 12 months)',
      'Director/Partner KYC'
    ],
    faqs: [
      { q: 'Do I need to provide collateral?', a: 'No, our standard business loans are unsecured. We also offer secured loans for higher amounts.' },
      { q: 'Can a startup apply?', a: 'Startups with at least 3 years of vintage and stable revenue are eligible.' }
    ]
  },
  {
    id: 2,
    title: 'Home Loan',
    icon: <HomeIcon />,
    shortDesc: 'Own your dream home today.',
    description: 'Stop renting and start owning. Our Home Loans come with attractive interest rates and long repayment tenures, making home ownership affordable. Available for ready-to-move properties, under-construction projects, and self-construction.',
    highlights: [
      { label: 'Interest Rate', value: '8.50% - 10.50% p.a.' },
      { label: 'Loan Amount', value: 'Up to 90% of Property Value' },
      { label: 'Tenure', value: 'Up to 30 Years' },
      { label: 'Processing Fee', value: '0.5% - 1% + GST' },
    ],
    features: [
      'PMAY Subsidy: Avail benefits under Pradhan Mantri Awas Yojana.',
      'Balance Transfer: Transfer existing loan for lower rates.',
      'Top-up Loan: Get additional funding on existing home loan.',
      'No Prepayment Charges: On floating rate loans for individuals.'
    ],
    eligibility: [
      { criteria: 'Age', value: '21 years to 65 years' },
      { criteria: 'Employment', value: 'Salaried / Self-Employed' },
      { criteria: 'CIBIL Score', value: '750+ for best rates' },
      { criteria: 'Income', value: 'Dependent on loan amount and city' }
    ],
    documents: [
      'Property Documents (Title Deed, Sale Agreement)',
      'KYC Documents',
      'Income Proof (Salary Slips / ITR)',
      'Bank Statements',
      'Processing Fee Cheque'
    ],
    faqs: [
      { q: 'What is the maximum tenure?', a: 'You can repay the loan over a period of up to 30 years.' },
      { q: 'Can I apply jointly?', a: 'Yes, adding a co-applicant (spouse/parent) can increase your loan eligibility.' }
    ]
  },
  {
    id: 3,
    title: 'Loan Against Property',
    icon: <DomainIcon />,
    shortDesc: 'Unlock value of your property.',
    description: 'Leverage your residential or commercial property to get high-value loans at lower interest rates. Loan Against Property (LAP) is ideal for big-ticket expenses like business expansion, child\'s higher education, or medical treatments.',
    highlights: [
      { label: 'Interest Rate', value: '9.50% - 12.00% p.a.' },
      { label: 'Loan Amount', value: 'Up to 70% of Market Value' },
      { label: 'Tenure', value: 'Up to 15 Years' },
      { label: 'Processing Fee', value: '1% - 2% + GST' },
    ],
    features: [
      'Lower Interest Rates: Cheaper than personal loans.',
      'High Loan Value: Get substantial funds based on property value.',
      'Continued Usage: Continue to use the property while it is mortgaged.',
      'Flexible End-use: Funds can be used for any legal purpose.'
    ],
    eligibility: [
      { criteria: 'Property Type', value: 'Residential, Commercial, Industrial' },
      { criteria: 'Property Status', value: 'Freehold, clear title, fully constructed' },
      { criteria: 'Income', value: 'Sufficient to service EMI' },
      { criteria: 'Age', value: '24 years to 65 years' }
    ],
    documents: [
      'Original Property Title Deeds',
      'Ownership Proof',
      'Income Documents',
      'Bank Statements',
      'KYC Documents'
    ],
    faqs: [
      { q: 'Is property insurance mandatory?', a: 'Yes, the property must be insured against fire and other perils.' },
      { q: 'Can I sell the property during the loan tenure?', a: 'No, the property is mortgaged with the lender. You must clear the loan to get the original papers back before selling.' }
    ]
  },
  {
    id: 4,
    title: 'Credit Card',
    icon: <CreditCardIcon />,
    shortDesc: 'Rewards, Cashback & more.',
    description: 'Discover a world of privileges with our range of Credit Cards. From lifetime free cards to premium travel cards, we help you choose the one that fits your lifestyle. Enjoy interest-free periods, reward points, and exclusive discounts.',
    highlights: [
      { label: 'Annual Fee', value: 'Nil to ₹5000 (varies)' },
      { label: 'Interest Free', value: 'Up to 50 Days' },
      { label: 'Finance Charges', value: '3.5% per month (on revolving)' },
      { label: 'Cash Withdrawal', value: 'Available (charges apply)' },
    ],
    features: [
      'Welcome Benefits: Bonus points or vouchers on joining.',
      'Milestone Rewards: Extra benefits on hitting spend targets.',
      'Fuel Surcharge Waiver: Save on fuel transactions.',
      'Airport Lounge Access: Complimentary visits on select cards.'
    ],
    eligibility: [
      { criteria: 'Age', value: '21 years to 60 years' },
      { criteria: 'Income', value: 'Min. ₹20,000/month' },
      { criteria: 'Credit Score', value: '750+ preferred' },
      { criteria: 'Employment', value: 'Stable employment history' }
    ],
    documents: [
      'PAN Card Copy',
      'Aadhar Card Copy',
      'Latest Salary Slip / ITR',
      'Passport Size Photograph'
    ],
    faqs: [
      { q: 'How do I avoid interest charges?', a: 'Pay the "Total Amount Due" by the payment due date every month.' },
      { q: 'What is the minimum amount due?', a: 'It is usually 5% of the outstanding amount, but paying only this attracts interest on the balance.' }
    ]
  }
];

const servicesHeroSx = {
  ...pageHeroRichSxFor(pageHeroImages.services),
  minHeight: servicesHeroMinHeight,
  py: { xs: 12, md: 17 },
};

const Services = () => {
  const [value, setValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const selectedService = servicesData[value];

  const handleScrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <Box
      id="services"
      sx={{
        bgcolor: brand.surfaceWarm,
        pt: 0,
        pb: { xs: 6, md: 8 },
      }}
      className="component-box component-box--flush"
    >
      <Box sx={{ ...servicesHeroSx, textAlign: 'center' }}>
        <Container maxWidth="lg">
          <Typography component="p" sx={{ ...pageHeroType.eyebrow, mb: 2.5, textAlign: 'center' }}>
            OUR OFFERINGS
          </Typography>
          <Typography component="h1" sx={{ ...pageHeroType.title, mb: 2.5, textAlign: 'center' }}>
            Our Premium Services
          </Typography>
          <Typography sx={{ ...pageHeroType.subtitle, mx: 'auto', textAlign: 'center' }}>
            Tailored financial solutions designed to elevate your lifestyle and business.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ pt: { xs: 4, md: 6 } }}>
        <Box>
          <Grid container spacing={4}>
            {/* Left Side Navigation (Tabs) */}
            <Grid item xs={12} md={3}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: '20px', 
                  overflow: 'hidden',
                  bgcolor: '#fff',
                  ...pageElevatedCardSx,
                }}
              >
                <Tabs
                  orientation={isMobile ? "horizontal" : "vertical"}
                  variant="scrollable"
                  value={value}
                  onChange={handleChange}
                  aria-label="Services tabs"
                  sx={{
                    '& .MuiTabs-indicator': {
                      backgroundColor: brand.primary,
                      width: '4px',
                      borderRadius: '4px'
                    },
                    '& .MuiTab-root': {
                      alignItems: 'center',
                      textAlign: 'left',
                      textTransform: 'none',
                      py: 3,
                      px: 3,
                      fontSize: '1rem',
                      fontWeight: 500,
                      minHeight: 70,
                      color: brand.muted,
                      transition: 'all 0.3s',
                      justifyContent: 'flex-start',
                      '&.Mui-selected': {
                        color: brand.primary,
                        background: '#FFFFFF',
                        fontWeight: 'bold',
                        boxShadow: '0 4px 16px rgba(13, 148, 136, 0.12)'
                      },
                      '&:hover': {
                        background: 'rgba(13, 148, 136, 0.08)',
                        color: brand.primary
                      }
                    }
                  }}
                >
                  {servicesData.map((service) => (
                    <Tab 
                      key={service.id} 
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Box sx={{ 
                            color: value === service.id ? brand.primary : 'inherit',
                            transition: 'color 0.3s'
                          }}>
                            {service.icon}
                          </Box>
                          <Typography variant="body1" fontWeight="inherit">{service.title}</Typography>
                        </Box>
                      } 
                    />
                  ))}
                </Tabs>
              </Paper>
              
              {/* Quick Contact Box */}
              <Paper 
                elevation={0} 
                sx={{ 
                  mt: 4, 
                  p: 4, 
                  borderRadius: '24px', 
                  background: brand.ctaGradient, 
                  color: 'white', 
                  display: { xs: 'none', md: 'block' },
                  position: 'relative',
                  overflow: 'hidden',
                  boxShadow: '0 16px 40px rgba(13, 148, 136, 0.35)'
                }}
              >
                  <Box sx={{ position: 'absolute', top: -20, right: -20, opacity: 0.1 }}>
                    <AccountBalanceIcon sx={{ fontSize: 100 }} />
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>Need Expert Advice?</Typography>
                  <Typography variant="body2" sx={{ mb: 3, opacity: 0.9 }}>
                      Our financial consultants are ready to help you make the best choice.
                  </Typography>
                  <Button 
                            variant="contained" 
                            fullWidth 
                            href="#/contact"
                            onClick={handleScrollTop}
                    sx={{ 
                      bgcolor: brand.accent, 
                      color: brand.slate,
                      fontWeight: 'bold',
                      '&:hover': { bgcolor: '#fcd34d' }
                    }}
                  >
                      Contact Us Now
                  </Button>
              </Paper>
            </Grid>

            {/* Right Side Content */}
            <Grid item xs={12} md={9}>
              <Paper 
                key={value} // Force re-render for animation when tab changes
                elevation={0} 
                sx={{ 
                  p: { xs: 3, md: 6 }, 
                  borderRadius: '24px', 
                  bgcolor: '#fff',
                  ...pageElevatedCardSx,
                  minHeight: '600px',
                  position: 'relative',
                }}
              >
                
                {/* Service Header */}
                <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', gap: 3, borderBottom: '1px solid rgba(0,0,0,0.05)', pb: 4 }}>
                  <Box sx={{ 
                    p: 3, 
                    borderRadius: '50%', 
                    bgcolor: brand.accent,
                    color: brand.slate,
                    boxShadow: '0 12px 28px rgba(245, 158, 11, 0.35)'
                  }}>
                     {React.cloneElement(selectedService.icon, { sx: { fontSize: 40 } })}
                  </Box>
                  <Box>
                      <Typography variant="h3" fontWeight="bold" sx={{ color: brand.primary, mb: 1 }}>
                          {selectedService.title}
                      </Typography>
                      <Typography variant="h6" sx={{ color: brand.muted }}>
                          {selectedService.shortDesc}
                      </Typography>
                  </Box>
                </Box>

                {/* Description */}
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', color: '#555', mb: 6, lineHeight: 1.8 }}>
                  {selectedService.description}
                </Typography>

                {/* Highlights Grid */}
                <Grid container spacing={3} sx={{ mb: 8 }}>
                  {selectedService.highlights.map((item, index) => (
                      <Grid item xs={6} sm={3} key={index}>
                          <Box sx={{ 
                            p: 3, 
                            background: brand.surface, 
                            borderRadius: '20px', 
                            textAlign: 'center', 
                            height: '100%',
                            transition: 'transform 0.3s',
                            '&:hover': { transform: 'translateY(-5px)', background: 'rgba(13, 148, 136, 0.08)' }
                          }}>
                              <Typography variant="caption" display="block" sx={{ color: brand.muted, mb: 1, textTransform: 'uppercase', letterSpacing: 1 }}>
                                  {item.label}
                              </Typography>
                              <Typography variant="h6" fontWeight="bold" sx={{ color: brand.primary }}>
                                  {item.value}
                              </Typography>
                          </Box>
                      </Grid>
                  ))}
                </Grid>

                {/* Two Column Layout for Features & Eligibility */}
                <Grid container spacing={6} sx={{ mb: 8 }}>
                  <Grid item xs={12} md={6}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: brand.primary, mb: 3 }}>
                          <KeyboardArrowRightIcon sx={{ color: brand.accent }} /> Key Features
                      </Typography>
                      <List dense>
                          {selectedService.features.map((feature, idx) => (
                              <ListItem key={idx} alignItems="flex-start" sx={{ px: 0 }}>
                                  <ListItemIcon sx={{ minWidth: 30, mt: 0.5 }}>
                                      <CheckCircleIcon sx={{ fontSize: 20, color: '#00C853' }} />
                                  </ListItemIcon>
                                  <ListItemText 
                                    primary={feature} 
                                    primaryTypographyProps={{ variant: 'body1', fontSize: '1rem', color: '#555' }} 
                                  />
                              </ListItem>
                          ))}
                      </List>
                  </Grid>
                  <Grid item xs={12} md={6}>
                      <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: brand.primary, mb: 3 }}>
                          <KeyboardArrowRightIcon sx={{ color: brand.accent }} /> Eligibility Criteria
                      </Typography>
                      <TableContainer component={Paper} elevation={0} sx={{ background: 'transparent', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 4 }}>
                          <Table size="small">
                              <TableBody>
                                  {selectedService.eligibility.map((row, idx) => (
                                      <TableRow key={idx} sx={{ '&:last-child td, &:last-child th': { border: 0 }, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                          <TableCell component="th" scope="row" sx={{ fontWeight: 'bold', color: '#666', width: '40%', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                              {row.criteria}
                                          </TableCell>
                                          <TableCell sx={{ color: '#333', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>{row.value}</TableCell>
                                      </TableRow>
                                  ))}
                              </TableBody>
                          </Table>
                      </TableContainer>
                  </Grid>
                </Grid>

                {/* Documents Section */}
                <Box sx={{ mb: 8 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: brand.indigo, mb: 3 }}>
                      <KeyboardArrowRightIcon sx={{ color: brand.accent }} /> Documents Required
                  </Typography>
                  <Grid container spacing={3}>
                      {selectedService.documents.map((doc, idx) => (
                          <Grid item xs={12} sm={6} key={idx}>
                              <Paper elevation={0} sx={{ 
                                p: 2.5, 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 2,
                                background: '#FAFAFA',
                                borderRadius: '16px',
                                border: '1px solid rgba(0,0,0,0.05)'
                              }}>
                                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: brand.accent }} />
                                  <Typography variant="body1" sx={{ color: '#555' }}>{doc}</Typography>
                              </Paper>
                          </Grid>
                      ))}
                  </Grid>
                </Box>

                {/* FAQs Accordion */}
                <Box sx={{ mb: 6 }}>
                  <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, color: brand.indigo }}>
                      Frequently Asked Questions
                  </Typography>
                  {selectedService.faqs.map((faq, idx) => (
                      <Accordion 
                        key={idx} 
                        elevation={0} 
                        sx={{ 
                          mb: 2, 
                          background: '#fff',
                          border: '1px solid rgba(0,0,0,0.05)',
                          color: '#333',
                          borderRadius: '12px !important',
                          '&:before': { display: 'none' } 
                        }}
                      >
                          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#999' }} />}>
                              <Typography fontWeight="medium" sx={{ color: '#333' }}>{faq.q}</Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                  {faq.a}
                              </Typography>
                          </AccordionDetails>
                      </Accordion>
                  ))}
                </Box>

                {/* CTA Button */}
                <Box sx={{ textAlign: 'center', mt: 8, pt: 6, borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                  <Button 
                      variant="contained" 
                      size="large" 
                      endIcon={<ArrowForwardIcon />} 
                      href="#/contact"
                      onClick={handleScrollTop}
                      sx={{ 
                        px: 8, 
                        py: 2, 
                        borderRadius: '50px', 
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        background: brand.ctaGradient,
                        color: 'white',
                        boxShadow: '0 14px 32px rgba(13, 148, 136, 0.35)',
                        '&:hover': {
                          opacity: 0.95,
                          transform: 'scale(1.05)'
                        },
                        transition: 'all 0.3s'
                      }}
                  >
                      Apply for {selectedService.title}
                  </Button>
                </Box>

              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Services;
